import { api } from '../services/API'

/**
 * Store externo da resposta a "devo ver estas páginas de Arrendamento?" — uma por endpoint de acesso
 * (`/proprietario/acesso`, `/inquilino/acesso`). Vive à parte dos hooks para o AuthContext o poder
 * invalidar no logout sem importar o próprio AuthContext (ciclo). Um só pedido por utilizador +
 * condomínio, partilhado por toda a app (Sidebar, CondominiumLayout, índice do módulo).
 */
export type RentalAccessStatus = 'loading' | 'visible' | 'hidden' | 'error'

export interface RentalAccessSnapshot {
    /** `utilizador:condomínio` a que a resposta diz respeito — `null` quando não há nada em cache. */
    key: string | null
    status: RentalAccessStatus
}

export interface RentalAccessStore {
    getSnapshot: () => RentalAccessSnapshot
    subscribe: (listener: () => void) => () => void
    /** Pede ao servidor a resposta para este utilizador/condomínio — uma só vez, só repete depois de uma falha. */
    load: (key: string) => void
    /** Descarta a cache — usar no logout, para o próximo login não herdar a resposta do anterior. */
    invalidate: () => void
}

const EMPTY: RentalAccessSnapshot = { key: null, status: 'loading' }

export function createRentalAccessStore(endpoint: string): RentalAccessStore {
    let snapshot: RentalAccessSnapshot = EMPTY
    const listeners = new Set<() => void>()

    const publish = (next: RentalAccessSnapshot) => {
        snapshot = next
        for (const listener of listeners) listener()
    }

    return {
        getSnapshot: () => snapshot,
        subscribe: (listener) => {
            listeners.add(listener)
            return () => {
                listeners.delete(listener)
            }
        },
        load: (key) => {
            if (snapshot.key === key && snapshot.status !== 'error') return
            publish({ key, status: 'loading' })
            api
                .get<{ visivel: boolean }>(endpoint)
                .then((response) => {
                    if (snapshot.key === key) publish({ key, status: response.data.visivel ? 'visible' : 'hidden' })
                })
                .catch(() => {
                    if (snapshot.key === key) publish({ key, status: 'error' })
                })
        },
        invalidate: () => publish(EMPTY),
    }
}
